/* eslint-disable max-len */

import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { PencilAltIcon } from 'assets/icons';
import { BlackButton, Box, Flex, IconButton, Input, MarginBox, Text } from 'UI';

export type BackgroundPositionCss = 'left' | 'center' | 'right' | 'top' | 'bottom' | string;
type Cursor = 'initial' | 'pointer';

const aspectRatioCss = css`
  height: 100%;
  aspect-ratio: 5/1;
`;

const disableTextAreaCss = css`
  display: none;
`;

const TextInfoWrapper = styled.div<{ disableTextArea: boolean }>`
  ${({ disableTextArea }) => disableTextArea && disableTextAreaCss}
`;

const SBackground = styled.div<{
  height?: number;
  backgroundPosition?: BackgroundPositionCss;
  cursor: Cursor;
  promoBannerAspect?: boolean;
}>`
  background-repeat: no-repeat;
  background-position: ${({ backgroundPosition }) => backgroundPosition ?? 'center'};
  background-size: cover;
  cursor: ${({ cursor }) => cursor};
  height: ${({ height }) => (height ? `${height}px` : undefined)};
  ${({ promoBannerAspect }) => promoBannerAspect && aspectRatioCss};
  display: flex;
  align-items: center;
`;

const SDiv = styled.div`
  max-width: 800px;
  min-width: 400px;
  background-color: white;
  padding: 20px;
  margin: 0px 100px;
`;

export type BannerImageSource = 'BASE64' | 'URL' | 'IMAGE_SRC';

export interface BannerProps {
  image: string;
  title?: string;
  description?: string;
  buttonLabel?: string;
  onClick?: () => void;
  backgroundPosition?: BackgroundPositionCss;
  dataType: BannerImageSource;
  height?: number;
  onlyImage?: boolean;
  dark?: boolean;
  cursor?: Cursor;
  promoBannerAspect?: boolean;
}

export interface EditableBannerProps extends BannerProps {
  handleChangeTitle: (title: string) => void;
  handleChangeDescription: (description: string) => void;
  disableTextArea?: boolean;
}

const getBackgroundImage = (image: string, dataType: BannerImageSource): string => {
  switch (dataType) {
    case 'BASE64':
      return `data:image/jpeg;base64,${image}`;
    case 'URL':
      return `${image}`;
    case 'IMAGE_SRC':
      return `/images/${image}`;
    default:
      return '';
  }
};

const BreakLineWrapper = styled.div<{ breakWord: boolean }>`
  overflow-wrap: break-word;
`;

export const Banner = ({
  image,
  title,
  description,
  buttonLabel,
  onClick,
  backgroundPosition,
  dataType,
  height,
  onlyImage = false,
  dark,
  cursor = 'initial',
  promoBannerAspect,
}: BannerProps) => {
  return (
    <SBackground
      backgroundPosition={backgroundPosition}
      onClick={onlyImage ? onClick : () => undefined}
      style={!onlyImage ? { backgroundImage: `url("${getBackgroundImage(image, dataType)}")` } : undefined}
      height={height}
      cursor={cursor}
      promoBannerAspect={promoBannerAspect}
    >
      {!onlyImage && (
        <Flex direction={'row'} align={'center'} justify={'flex-start'} maxHeight={height} minHeight={height}>
          <SDiv>
            <BreakLineWrapper breakWord={!title?.includes(' ')}>
              <Text type={dark ? 'h1_banner_dark' : 'h1_banner_light'}>{title}</Text>
            </BreakLineWrapper>
            <BreakLineWrapper breakWord={!description?.includes(' ')}>
              <Text type="base" color={dark ? 'white' : undefined}>
                {description}
              </Text>
            </BreakLineWrapper>
            <Box height={32} />
            {buttonLabel && (
              <BlackButton onClick={onClick} size={'xlarge'}>
                {buttonLabel}
              </BlackButton>
            )}
          </SDiv>
        </Flex>
      )}
      {onlyImage && <img alt="ads" src={getBackgroundImage(image, dataType)} style={{ width: '100%' }} />}
    </SBackground>
  );
};

export const EditableBanner = ({
  image,
  title,
  description,
  buttonLabel,
  backgroundPosition,
  dataType,
  cursor = 'initial',
  handleChangeTitle,
  handleChangeDescription,
  disableTextArea = false,
}: EditableBannerProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const switchEdittingMode = () => {
    setIsEditing((prevState) => !prevState);
  };

  return (
    <SBackground
      backgroundPosition={backgroundPosition}
      style={{ backgroundImage: `url("${getBackgroundImage(image, dataType)}")` }}
      cursor={cursor}
      promoBannerAspect
    >
      <TextInfoWrapper disableTextArea={disableTextArea}>
        <Flex alignSelf={'center'} minHeight={'100%'} maxHeight={'100%'} padding={'20px 75px 20px 75px'}>
          <Flex background={'white'} minWidth={'400px'} maxWidth={'600px'} direction={'column'} padding={8} gap={8}>
            {isEditing ? (
              <Input bordered initialValue={title} onBlur={handleChangeTitle} />
            ) : (
              <Text type="heading4" disableGutter>
                {title}
              </Text>
            )}
            {isEditing ? (
              <Input bordered initialValue={description} onBlur={handleChangeDescription} />
            ) : (
              <Text type="base" disableGutter>
                {description}
              </Text>
            )}
            <MarginBox mt={8} />
            <Flex justify={'space-between'}>
              <BlackButton stretch={false} size={'middle'}>
                {buttonLabel}
              </BlackButton>
              <IconButton
                IconComponent={PencilAltIcon}
                size={24}
                onClick={switchEdittingMode}
                color={'clear_blue'}
                hoverColor={'clear_blue'}
                boxSize={'smd'}
                bgColor={'grey85'}
                hoverFill={'grey85'}
              />
            </Flex>
          </Flex>
        </Flex>
      </TextInfoWrapper>
    </SBackground>
  );
};
